import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { financeiroApi, clientApi } from '../../../services/api';
import type { ContaBancaria, CategoriaFinanceira, Client } from '../../../types';
import { DocumentArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface FormData {
  conta_bancaria: string;
  client: string;
  tipo_categoria: 'servicos' | 'locacao' | '';
  data_vencimento: string;
  valor: string;
  forma_pagamento: string;
  titulo?: string;
  observacoes?: string;
  descricao: string;
  chave_nfe: string;
  numero_nfe: string;
  serie_nfe: string;
  valor_icms: string;
  valor_icms_st: string;
  valor_pis: string;
  valor_cofins: string;
  valor_ipi: string;
  valor_ii: string;
  valor_iss: string;
  valor_inss: string;
  valor_ir: string;
  valor_csll: string;
  valor_frete: string;
  valor_seguro: string;
  valor_desconto_nfe: string;
  valor_outras_despesas: string;
  valor_total_tributos: string;
}

interface ContaReceberFormProps {
  mode?: 'create' | 'edit';
}

export const ContaReceberForm: React.FC<ContaReceberFormProps> = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit' || !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasNFSeData, setHasNFSeData] = useState(false);
  const isReadOnly = isEdit && hasNFSeData;

  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [categorias, setCategorias] = useState<CategoriaFinanceira[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchClientTerm, setSearchClientTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [uploadingXML, setUploadingXML] = useState(false);

  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    conta_bancaria: '',
    client: '',
    tipo_categoria: 'servicos',
    data_vencimento: new Date().toISOString().split('T')[0],
    valor: '',
    forma_pagamento: '',
    descricao: '',
    titulo: '',
    observacoes: '',
    chave_nfe: '',
    numero_nfe: '',
    serie_nfe: '',
    valor_icms: '0.00',
    valor_icms_st: '0.00',
    valor_pis: '0.00',
    valor_cofins: '0.00',
    valor_ipi: '0.00',
    valor_ii: '0.00',
    valor_iss: '0.00',
    valor_inss: '0.00',
    valor_ir: '0.00',
    valor_csll: '0.00',
    valor_frete: '0.00',
    valor_seguro: '0.00',
    valor_desconto_nfe: '0.00',
    valor_outras_despesas: '0.00',
    valor_total_tributos: '0.00',
  });

  useEffect(() => {
    const init = async () => {
      const loadedCategorias = await loadInitialData();
      if (isEdit && id && loadedCategorias && loadedCategorias.length > 0) {
        await loadConta(loadedCategorias);
      }
    };
    init();
  }, [isEdit, id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchClientTerm.length >= 3) {
        loadClients(searchClientTerm);
        setShowClientDropdown(true);
      } else if (searchClientTerm.length === 0) {
        setClients([]);
        setShowClientDropdown(false);
      } else {
        setShowClientDropdown(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchClientTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadInitialData = async (): Promise<CategoriaFinanceira[]> => {
    try {
      setLoading(true);
      const [contasRes, categoriasRes, clientsRes] = await Promise.all([
        financeiroApi.getContas({ ativa: true }),
        financeiroApi.getCategorias({ tipo: 'R', ativa: true, page_size: 500 }),
        clientApi.getClients({ page_size: 50, is_active: true })
      ]);
      const loadedCategorias = categoriasRes.results || [];
      setContas(contasRes.results || []);
      setCategorias(loadedCategorias);
      setClients(clientsRes.results || []);
      return loadedCategorias;
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async (search: string) => {
    try {
      const response = await clientApi.getClients({ search, is_active: true, page_size: 50 });
      setClients(response.results || []);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
    }
  };

  const formatDecimal = (value: number | string | undefined): string => {
    if (value === undefined || value === null) return '0.00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const loadConta = async (loadedCategorias: CategoriaFinanceira[]) => {
    try {
      const conta: any = await financeiroApi.getContaReceber(Number(id));
      const categoriaId = conta.categoria?.id || conta.categoria;
      const contaBancariaId = conta.conta_bancaria?.id || conta.conta_bancaria;
      const clientId = conta.client;
      const hasNFe = !!conta.numero_nfe || !!conta.chave_nfe || (conta.observacoes && conta.observacoes.includes('Importado'));
      setHasNFSeData(hasNFe);

      if (clientId) {
        try {
          const clientData = await clientApi.getClient(clientId.toString());
          setSelectedClientName(clientData.name || '');
        } catch (err) {
          console.error('Erro ao buscar nome do cliente:', err);
        }
      }

      let tipoCategoria: 'servicos' | 'locacao' = 'servicos';
      if (categoriaId) {
        const categoriaIdStr = categoriaId.toString();
        const categoria = loadedCategorias.find(cat => cat.id.toString() === categoriaIdStr);
        if (categoria) {
          const nomeCategoria = categoria.nome.toLowerCase();
          if (nomeCategoria.includes('locação') || nomeCategoria.includes('locacao')) {
            tipoCategoria = 'locacao';
          }
        }
      }

      setFormData({
        conta_bancaria: contaBancariaId ? contaBancariaId.toString() : '',
        client: clientId ? clientId.toString() : '',
        tipo_categoria: tipoCategoria,
        data_vencimento: conta.data_vencimento || '',
        valor: conta.valor ? conta.valor.toString() : '',
        forma_pagamento: conta.forma_pagamento || '',
        descricao: conta.descricao || '',
        titulo: conta.titulo || '',
        observacoes: conta.observacoes || '',
        chave_nfe: conta.chave_nfe || '',
        numero_nfe: conta.numero_nfe || '',
        serie_nfe: conta.serie_nfe || '',
        valor_icms: formatDecimal(conta.valor_icms),
        valor_icms_st: formatDecimal(conta.valor_icms_st),
        valor_pis: formatDecimal(conta.valor_pis),
        valor_cofins: formatDecimal(conta.valor_cofins),
        valor_ipi: formatDecimal(conta.valor_ipi),
        valor_ii: formatDecimal(conta.valor_ii),
        valor_iss: formatDecimal(conta.valor_iss),
        valor_inss: formatDecimal(conta.valor_inss),
        valor_ir: formatDecimal(conta.valor_ir),
        valor_csll: formatDecimal(conta.valor_csll),
        valor_frete: formatDecimal(conta.valor_frete),
        valor_seguro: formatDecimal(conta.valor_seguro),
        valor_desconto_nfe: formatDecimal(conta.valor_desconto_nfe),
        valor_outras_despesas: formatDecimal(conta.valor_outras_despesas),
        valor_total_tributos: formatDecimal(conta.valor_total_tributos),
      });
    } catch (err) {
      console.error('Erro ao carregar conta:', err);
      alert('Erro ao carregar dados da conta');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const formatarValorMonetario = (valor: string): string => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (!apenasNumeros) return '';
    const valorNumerico = parseInt(apenasNumeros, 10) / 100;
    return valorNumerico.toFixed(2).replace('.', ',');
  };

  const valorParaNumero = (valor: string): number => {
    if (!valor) return 0;
    return parseFloat(valor.replace(',', '.')) || 0;
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = formatarValorMonetario(e.target.value);
    setFormData(prev => ({ ...prev, valor: valorFormatado }));
    if (errors.valor) setErrors(prev => ({ ...prev, valor: '' }));
  };

  const handleXMLUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xml')) {
      alert('Por favor, selecione um arquivo XML válido');
      return;
    }
    try {
      setUploadingXML(true);
      const data = await financeiroApi.parseNFeXML(file);
      setHasNFSeData(true);
      const valorTotal = data.valores?.valor_total || '0';
      const titulo = data.numero_nota ? `NF-e ${data.numero_nota}` : '';
      let clientId = '';
      let clientMessage = '';
      if (data.client_info && data.client_info.id) {
        clientId = data.client_info.id;
        setSelectedClientName(data.client_info.name || '');
        setSearchClientTerm(data.client_info.name || '');
        clientMessage = data.client_info.created
          ? `\nCliente criado: ${data.client_info.name}`
          : `\nCliente encontrado: ${data.client_info.name}`;
      }
      const descricaoBase = data.tomador?.razao_social
        ? `Serviço para ${data.tomador.razao_social}`
        : (data.emitente?.razao_social ? `Serviço - ${data.emitente.razao_social}` : '');

      setFormData(prev => ({
        ...prev,
        client: clientId,
        valor: valorTotal,
        data_vencimento: data.data_emissao || prev.data_vencimento,
        titulo: titulo,
        descricao: descricaoBase || prev.descricao,
        observacoes: `Importado em ${new Date().toLocaleString('pt-BR')}`,
        chave_nfe: data.chave_acesso || '',
        numero_nfe: data.numero_nota || '',
        serie_nfe: data.serie || '',
        valor_icms: formatDecimal(data.impostos?.icms),
        valor_icms_st: formatDecimal(data.impostos?.icms_st),
        valor_pis: formatDecimal(data.impostos?.pis),
        valor_cofins: formatDecimal(data.impostos?.cofins),
        valor_ipi: formatDecimal(data.impostos?.ipi),
        valor_ii: formatDecimal(data.impostos?.ii),
        valor_iss: formatDecimal(data.impostos?.iss),
        valor_inss: formatDecimal(data.impostos?.inss),
        valor_ir: formatDecimal(data.impostos?.ir),
        valor_csll: formatDecimal(data.impostos?.csll),
        valor_frete: formatDecimal(data.valores?.valor_frete),
        valor_seguro: formatDecimal(data.valores?.valor_seguro),
        valor_desconto_nfe: formatDecimal(data.valores?.valor_desconto),
        valor_outras_despesas: formatDecimal(data.valores?.valor_outras_despesas),
        valor_total_tributos: formatDecimal(data.impostos?.total_tributos),
      }));
      alert(`XML processado!\nNF-e: ${data.numero_nota}\nValor: R$ ${parseFloat(valorTotal).toFixed(2)}${clientMessage}`);
    } catch (error: any) {
      console.error('Erro ao processar XML:', error);
      alert(error.response?.data?.error || 'Erro ao processar XML.');
    } finally {
      setUploadingXML(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const valorNumerico = valorParaNumero(formData.valor);
    if (!formData.valor || valorNumerico <= 0) newErrors.valor = 'Valor obrigatório';
    if (!formData.conta_bancaria) newErrors.conta_bancaria = 'Conta obrigatória';
    if (!formData.client) newErrors.client = 'Cliente obrigatório';
    if (!formData.tipo_categoria) newErrors.categoria = 'Categoria obrigatória';
    if (!formData.data_vencimento) newErrors.data_vencimento = 'Data obrigatória';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setErrors({});
    try {
      const categoriaServicos = categorias.find(cat => cat.nome.toLowerCase() === 'serviços' || cat.nome.toLowerCase() === 'servicos');
      const categoriaLocacao = categorias.find(cat => cat.nome.toLowerCase() === 'locação' || cat.nome.toLowerCase() === 'locacao');
      const categoriaId = formData.tipo_categoria === 'locacao' ? categoriaLocacao?.id : categoriaServicos?.id;

      const contaData: any = {
        descricao: formData.descricao || undefined,
        valor: valorParaNumero(formData.valor),
        data_vencimento: formData.data_vencimento,
        categoria_id: categoriaId,
        client: formData.client && formData.client !== '' ? formData.client : null,
        conta_bancaria_id: Number(formData.conta_bancaria),
        forma_pagamento: formData.forma_pagamento || undefined,
        titulo: formData.titulo || undefined,
        observacoes: formData.observacoes || undefined,
        chave_nfe: formData.chave_nfe || null,
        numero_nfe: formData.numero_nfe || '',
        serie_nfe: formData.serie_nfe || '',
        valor_icms: parseFloat(formData.valor_icms) || 0,
        valor_icms_st: parseFloat(formData.valor_icms_st) || 0,
        valor_pis: parseFloat(formData.valor_pis) || 0,
        valor_cofins: parseFloat(formData.valor_cofins) || 0,
        valor_ipi: parseFloat(formData.valor_ipi) || 0,
        valor_ii: parseFloat(formData.valor_ii) || 0,
        valor_iss: parseFloat(formData.valor_iss) || 0,
        valor_inss: parseFloat(formData.valor_inss) || 0,
        valor_ir: parseFloat(formData.valor_ir) || 0,
        valor_csll: parseFloat(formData.valor_csll) || 0,
        valor_frete: parseFloat(formData.valor_frete) || 0,
        valor_seguro: parseFloat(formData.valor_seguro) || 0,
        valor_desconto_nfe: parseFloat(formData.valor_desconto_nfe) || 0,
        valor_outras_despesas: parseFloat(formData.valor_outras_despesas) || 0,
        valor_total_tributos: parseFloat(formData.valor_total_tributos) || 0,
      };

      if (isEdit && id) {
        await financeiroApi.updateContaReceber(Number(id), contaData);
      } else {
        await financeiroApi.createContaReceber(contaData);
      }
      navigate('/financeiro/contas-receber');
    } catch (err: any) {
      console.error('Erro ao salvar:', err);
      if (err.response?.data) setErrors(err.response.data);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-24">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div>
        <h1 className="text-base font-bold text-secondary-900">
          {isReadOnly ? 'Visualizar Conta a Receber' : isEdit ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
        </h1>
        <p className="text-xs text-secondary-600 mt-0.5">
          {isReadOnly ? 'Dados importados de NF-e' : isEdit ? 'Atualize as informações' : 'Cadastre uma nova conta'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2">
        {/* Banner somente leitura */}
        {isReadOnly && (
          <div className="bg-amber-50 border border-amber-300 rounded px-2 py-1.5 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <span className="text-xs text-amber-800">
              <strong>NF-e {formData.numero_nfe}</strong> - Somente leitura
            </span>
          </div>
        )}

        {/* Upload XML */}
        {!isEdit && !hasNFSeData && (
          <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1.5 flex items-center gap-2">
            <DocumentArrowUpIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="text-xs text-blue-800">Importar NF-e:</span>
            <input ref={fileInputRef} type="file" accept=".xml" onChange={handleXMLUpload} disabled={uploadingXML} className="hidden" id="xml-upload" />
            <label htmlFor="xml-upload" className={`px-2 py-0.5 text-xs rounded cursor-pointer ${uploadingXML ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              {uploadingXML ? 'Processando...' : 'Escolher XML'}
            </label>
          </div>
        )}

        {/* Dados Principais */}
        <div className="bg-white border border-secondary-200 rounded p-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Conta Bancária *</label>
              <select
                name="conta_bancaria"
                value={formData.conta_bancaria}
                onChange={handleChange}
                disabled={isReadOnly}
                className={`w-full px-2 py-1 text-xs border rounded ${isReadOnly ? 'bg-gray-100' : ''} ${errors.conta_bancaria ? 'border-red-300' : 'border-secondary-300'}`}
              >
                <option value="">Selecione...</option>
                {contas.map((conta) => (
                  <option key={conta.id} value={conta.id}>{conta.nome}</option>
                ))}
              </select>
              {errors.conta_bancaria && <p className="text-red-600 text-xs mt-0.5">{errors.conta_bancaria}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Cliente *</label>
              <div ref={clientDropdownRef} className="relative">
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={selectedClientName || searchClientTerm}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      setSearchClientTerm(e.target.value);
                      setSelectedClientName('');
                      setFormData(prev => ({ ...prev, client: '' }));
                    }
                  }}
                  onFocus={() => { if (clients.length > 0 && !isReadOnly) setShowClientDropdown(true); }}
                  readOnly={isReadOnly}
                  className={`w-full px-2 py-1 text-xs border rounded ${isReadOnly ? 'bg-gray-100' : ''} ${errors.client ? 'border-red-300' : 'border-secondary-300'}`}
                />
                {showClientDropdown && clients.length > 0 && !isReadOnly && (
                  <div className="absolute z-50 w-full mt-0.5 bg-white border border-secondary-300 rounded shadow-lg max-h-40 overflow-y-auto">
                    {clients.map((client) => (
                      <div
                        key={client.id}
                        className="px-2 py-1 hover:bg-blue-50 cursor-pointer text-xs border-b border-secondary-100"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, client: client.id.toString() }));
                          setSelectedClientName(client.name);
                          setSearchClientTerm('');
                          setShowClientDropdown(false);
                        }}
                      >
                        <div className="font-medium">{client.name}</div>
                        {client.cpf_cnpj && <div className="text-secondary-500">{client.cpf_cnpj}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.client && <p className="text-red-600 text-xs mt-0.5">{errors.client}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Vencimento *</label>
              <input
                type="date"
                name="data_vencimento"
                value={formData.data_vencimento}
                onChange={handleChange}
                disabled={isReadOnly}
                className={`w-full px-2 py-1 text-xs border rounded ${isReadOnly ? 'bg-gray-100' : ''} ${errors.data_vencimento ? 'border-red-300' : 'border-secondary-300'}`}
              />
              {errors.data_vencimento && <p className="text-red-600 text-xs mt-0.5">{errors.data_vencimento}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Valor *</label>
              <input
                type="text"
                inputMode="numeric"
                name="valor"
                value={formData.valor}
                onChange={isReadOnly ? undefined : handleValorChange}
                readOnly={isReadOnly}
                placeholder="0,00"
                className={`w-full px-2 py-1 text-xs border rounded ${isReadOnly ? 'bg-gray-100' : ''} ${errors.valor ? 'border-red-300' : 'border-secondary-300'}`}
              />
              {errors.valor && <p className="text-red-600 text-xs mt-0.5">{errors.valor}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Categoria *</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="tipo_categoria"
                    value="servicos"
                    checked={formData.tipo_categoria === 'servicos'}
                    onChange={() => !isReadOnly && setFormData(prev => ({ ...prev, tipo_categoria: 'servicos' }))}
                    disabled={isReadOnly}
                    className="w-3 h-3"
                  />
                  <span className="text-xs">Serviços</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="tipo_categoria"
                    value="locacao"
                    checked={formData.tipo_categoria === 'locacao'}
                    onChange={() => !isReadOnly && setFormData(prev => ({ ...prev, tipo_categoria: 'locacao' }))}
                    disabled={isReadOnly}
                    className="w-3 h-3"
                  />
                  <span className="text-xs">Locação</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Forma Recebimento</label>
              <select
                name="forma_pagamento"
                value={formData.forma_pagamento}
                onChange={handleChange}
                className="w-full px-2 py-1 text-xs border border-secondary-300 rounded"
              >
                <option value="">Selecione...</option>
                <option value="DIN">Dinheiro</option>
                <option value="PIX">PIX</option>
                <option value="TED">TED</option>
                <option value="BOL">Boleto</option>
                <option value="CC">Cartão Crédito</option>
                <option value="CD">Cartão Débito</option>
                <option value="CHQ">Cheque</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Título</label>
              <input
                type="text"
                name="titulo"
                value={formData.titulo || ''}
                onChange={handleChange}
                readOnly={isReadOnly}
                placeholder="Ex: NF-e 12345"
                className={`w-full px-2 py-1 text-xs border rounded ${isReadOnly ? 'bg-gray-100' : 'border-secondary-300'}`}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Descrição</label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                readOnly={isReadOnly}
                className={`w-full px-2 py-1 text-xs border rounded ${isReadOnly ? 'bg-gray-100' : 'border-secondary-300'}`}
              />
            </div>
          </div>

          <div className="mt-2">
            <label className="block text-xs font-medium text-secondary-700 mb-0.5">Observações</label>
            <textarea
              name="observacoes"
              value={formData.observacoes || ''}
              onChange={handleChange}
              rows={1}
              className="w-full px-2 py-1 text-xs border border-secondary-300 rounded"
            />
          </div>
        </div>

        {/* Dados NF-e */}
        {(hasNFSeData || isEdit) && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Dados da Nota Fiscal</h3>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-secondary-600 mb-0.5">Número</label>
                <input type="text" value={formData.numero_nfe} readOnly className="w-full px-2 py-1 text-xs border border-secondary-200 rounded bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-secondary-600 mb-0.5">Série</label>
                <input type="text" value={formData.serie_nfe} readOnly className="w-full px-2 py-1 text-xs border border-secondary-200 rounded bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-secondary-600 mb-0.5">Chave</label>
                <input type="text" value={formData.chave_nfe} readOnly className="w-full px-2 py-1 text-xs border border-secondary-200 rounded bg-gray-50 truncate" title={formData.chave_nfe} />
              </div>
            </div>
          </div>
        )}

        {/* Impostos */}
        {(hasNFSeData || isEdit) && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Impostos</h3>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
              {[
                { label: 'ICMS', value: formData.valor_icms },
                { label: 'ICMS-ST', value: formData.valor_icms_st },
                { label: 'PIS', value: formData.valor_pis },
                { label: 'COFINS', value: formData.valor_cofins },
                { label: 'IPI', value: formData.valor_ipi },
                { label: 'II', value: formData.valor_ii },
                { label: 'ISS', value: formData.valor_iss },
                { label: 'INSS', value: formData.valor_inss },
                { label: 'IR', value: formData.valor_ir },
                { label: 'CSLL', value: formData.valor_csll },
              ].map((item) => (
                <div key={item.label}>
                  <label className="block text-xs text-secondary-500 mb-0.5">{item.label}</label>
                  <input type="text" value={item.value} readOnly className="w-full px-1 py-0.5 text-xs border border-secondary-200 rounded bg-gray-50 text-center" />
                </div>
              ))}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-secondary-600">Total Tributos:</span>
              <span className="text-xs font-semibold text-blue-700">{formData.valor_total_tributos}</span>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end gap-2 pt-2 border-t border-secondary-200">
          <button
            type="button"
            onClick={() => navigate('/financeiro/contas-receber')}
            className="px-3 py-1 text-xs font-medium text-secondary-700 bg-white border border-secondary-300 rounded hover:bg-secondary-50"
            disabled={saving}
          >
            {isReadOnly ? 'Voltar' : 'Cancelar'}
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:bg-gray-400"
              disabled={saving}
            >
              {saving ? 'Salvando...' : isEdit ? 'Atualizar' : 'Salvar'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContaReceberForm;
